import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { tenantKey } from '@/lib/queryClient'
import { useTenant } from '@/hooks/useTenant'
import { apApi } from './api'
import type { BillPayload, VendorPayload } from './types'

// ---------- Stats ----------

export function useApSummary(window?: { from: string; to: string }) {
  const tenantId = useTenant()
  // Why: same window-aware caching as the AR side so selecting "Last 6
  // Months" on the dashboard refetches AP without colliding with the
  // platform admin tile that asks for the default month-to-date view.
  const sig = window?.from && window?.to ? `${window.from}_${window.to}` : 'mtd'
  return useQuery({
    queryKey: tenantKey(tenantId, 'ap', 'summary', sig),
    queryFn: () => apApi.apSummary(window),
  })
}

// ---------- Vendors ----------

export function useVendors(params?: { search?: string; isActive?: boolean }) {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'ap', 'vendors', params || {}),
    queryFn: () => apApi.listVendors(params),
  })
}

export function useVendor(id: number | undefined) {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'ap', 'vendor', id || 0),
    queryFn: () => apApi.getVendor(id as number),
    enabled: !!id,
  })
}

export function useCreateVendor() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: (body: VendorPayload) => apApi.createVendor(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'ap', 'vendors') }),
  })
}

export function useUpdateVendor() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: VendorPayload }) => apApi.updateVendor(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'ap', 'vendors') })
      qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'ap', 'vendor') })
    },
  })
}

export function useDeactivateVendor() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: (id: number) => apApi.deactivateVendor(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'ap', 'vendors') }),
  })
}

// ---------- Vendor bank accounts ----------

export function useVendorBankAccounts(vendorId: number | undefined) {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'ap', 'vendor-banks', vendorId || 0),
    queryFn: () => apApi.listBankAccounts(vendorId as number),
    enabled: !!vendorId,
  })
}

export function useUpsertVendorBankAccount(vendorId: number) {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: (body: Partial<import('./types').VendorBankAccount>) =>
      apApi.upsertBankAccount(vendorId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'ap', 'vendor-banks', vendorId) }),
  })
}

// ---------- Bills ----------

export function useBills(params?: { status?: string; vendor_id?: number }) {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'ap', 'bills', params || {}),
    queryFn: () => apApi.listBills(params),
  })
}

export function useBill(id: number | undefined) {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'ap', 'bill', id || 0),
    queryFn: () => apApi.getBill(id as number),
    enabled: !!id,
  })
}

function invalidateBills(qc: ReturnType<typeof useQueryClient>, tenantId: string | null, id?: number) {
  qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'ap', 'bills') })
  if (id !== undefined) qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'ap', 'bill', id) })
  qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'reporting') })
}

export function useCreateBill() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: (body: BillPayload) => apApi.createBill(body),
    onSuccess: (res) => invalidateBills(qc, tenantId, res.bill.id),
  })
}

export function useSubmitBill() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: (id: number) => apApi.submitBill(id),
    onSuccess: (_res, id) => {
      invalidateBills(qc, tenantId, id)
      qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'approvals') })
    },
  })
}

export function usePostBill() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: (id: number) => apApi.postBill(id),
    onSuccess: (_res, id) => invalidateBills(qc, tenantId, id),
  })
}

export function useApAging(asOf?: string) {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'ap', 'ap-aging', asOf || 'today'),
    queryFn: () => apApi.apAging(asOf ? { as_of: asOf } : undefined),
  })
}

export function usePayBill() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: { amount: number; method?: string; reference?: string; send_email?: boolean } }) =>
      apApi.payBill(id, body),
    onSuccess: (_res, vars) => invalidateBills(qc, tenantId, vars.id),
  })
}
